import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import {
  Bell,
  HelpCircle,
  LogOut,
  ChevronLeft,
  Menu,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Avatar } from '../ui/Avatar';
import { ConfirmModal } from '../ui/Modal';
import { getNavItems, getDashboardBase } from '@/backoffice/config/navigation';
import { getRoleTheme, type BackofficeUserType } from '@/backoffice/config/theme';
import { useAdminNavBadges } from '@/backoffice/hooks/useAdminNavBadges';
import { useActiveNavPath } from '@/backoffice/hooks/useBackofficeRole';
import { logout } from '@/domains/auth/services/auth.service';

interface SidebarProps {
  userType: BackofficeUserType;
  userName?: string;
  userAvatar?: string;
}

export function Sidebar({ userType, userName, userAvatar }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const navItems = getNavItems(userType);
  const adminBadges = useAdminNavBadges(userType);
  const activeNavPath = useActiveNavPath(userType);
  const theme = getRoleTheme(userType);
  const dashboardBase = getDashboardBase(userType);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const isActive = (path: string) => path === activeNavPath;

  const isFooterActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      setShowLogoutConfirm(false);
      navigate('/login');
    } finally {
      setLoggingOut(false);
    }
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-4 py-5 border-b border-gray-100">
        <Link to={navItems[0]?.path ?? '/'} className="flex items-center gap-3">
          <div
            className={cn(
              'w-10 h-10 bg-gradient-to-br rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg',
              theme.logoGradient,
              theme.logoShadow,
            )}
          >
            O
          </div>
          {!collapsed && (
            <div>
              <span className="text-xl font-bold text-gray-900">Onapila</span>
              <span className="block text-xs text-gray-500">{theme.label}</span>
            </div>
          )}
        </Link>
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const dynamicBadge =
              userType === 'admin' || userType === 'super_admin'
                ? (adminBadges[item.key as keyof typeof adminBadges] ?? item.badge)
                : item.badge;

            return (
            <li key={item.key}>
              <Link
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                  isActive(item.path)
                    ? cn(theme.activeNav, theme.activeNavText)
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                )}
              >
                <span className={isActive(item.path) ? theme.activeIcon : ''}>{item.icon}</span>
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {dynamicBadge != null && dynamicBadge > 0 && (
                      <span
                        className={cn(
                          'px-2 py-0.5 text-xs rounded-full font-semibold',
                          theme.badge,
                          theme.badgeText,
                        )}
                      >
                        {dynamicBadge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            </li>
            );
          })}
        </ul>
      </nav>

      <div className="px-3 py-4 border-t border-gray-100">
        <ul className="space-y-1">
          <li>
            <Link
              to={`${dashboardBase}/notifications`}
              onClick={() => setMobileOpen(false)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                isFooterActive(`${dashboardBase}/notifications`)
                  ? cn(theme.activeNav, theme.activeNavText)
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              )}
            >
              <Bell size={20} />
              {!collapsed && <span>Notifications</span>}
            </Link>
          </li>
          <li>
            <Link
              to={`${dashboardBase}/aide`}
              onClick={() => setMobileOpen(false)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                isFooterActive(`${dashboardBase}/aide`)
                  ? cn(theme.activeNav, theme.activeNavText)
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              )}
            >
              <HelpCircle size={20} />
              {!collapsed && <span>Aide</span>}
            </Link>
          </li>
          <li>
            <button
              type="button"
              onClick={() => setShowLogoutConfirm(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-all duration-200"
            >
              <LogOut size={20} />
              {!collapsed && <span>Déconnexion</span>}
            </button>
          </li>
        </ul>
      </div>

      <div
        className={cn(
          'px-4 py-3 border-t border-gray-100 flex items-center',
          collapsed ? 'justify-center' : 'gap-3',
        )}
      >
        <Avatar name={userName} src={userAvatar} size="sm" />
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{userName}</p>
            <p className="text-xs text-gray-500">{theme.label}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-lg border border-gray-200"
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="fixed inset-0 bg-black/20" onClick={() => setMobileOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-72 bg-white shadow-xl">
            <SidebarContent />
          </div>
        </div>
      )}

      <aside
        className={cn(
          'hidden lg:flex flex-col bg-white border-r border-gray-200 h-screen sticky top-0 transition-all duration-300 relative',
          collapsed ? 'w-20' : 'w-64',
        )}
      >
        <SidebarContent />
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-16 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow hover:bg-gray-50"
        >
          <ChevronLeft size={14} className={cn('transition-transform', collapsed && 'rotate-180')} />
        </button>
      </aside>

      <ConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => !loggingOut && setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title="Fermer la session ?"
        message="Vous devrez ressaisir vos identifiants Onapila pour vous reconnecter."
        confirmText="Se déconnecter"
        cancelText="Annuler"
        variant="danger"
        isLoading={loggingOut}
      />
    </>
  );
}

export type { BackofficeUserType };
